import styled, { keyframes } from "styled-components";

interface AccentProps {
  accent?: string;
  glow?: string;
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
  display: flex;
  height: 95vh;
`;

export const MainContent = styled.div`
  flex: 1;
  padding: 24px 28px 40px;
  background: transparent;
  overflow-y: auto;
  max-height: 100vh;
`;

/* ----------------------------- Header ----------------------------- */
export const Header = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 26px;
  flex-wrap: wrap;
  animation: ${fadeUp} 0.5s ease both;
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  span.eyebrow {
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
  }

  h1 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--text);
    line-height: 1.1;
  }

  p {
    margin: 0;
    color: var(--text-muted);
    font-size: 0.95rem;
  }
`;

export const DatePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  color: var(--text);
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: capitalize;

  svg {
    color: var(--accent);
  }
`;

/* --------------------------- Stat cards --------------------------- */
export const StatGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 18px;
  margin-bottom: 24px;
`;

export const StatCard = styled.article<AccentProps & { delay?: number }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  overflow: hidden;
  transition: transform 0.22s ease, box-shadow 0.22s ease,
    border-color 0.22s ease;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${(p) => (p.delay ?? 0) * 60}ms;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${(p) => p.accent || "var(--accent)"};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: ${(p) => p.accent || "var(--border-strong)"};
  }
`;

export const StatIcon = styled.div<AccentProps>`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: ${(p) => p.accent || "var(--accent)"};
  background: ${(p) =>
    p.glow ||
    `color-mix(in srgb, ${p.accent || "var(--accent)"} 15%, transparent)`};

  svg {
    font-size: 26px;
  }
`;

export const StatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  label {
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--text-muted);
    line-height: 1.2;
  }

  h2 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.7rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--text);
    line-height: 1.1;
  }

  small {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--text-dim);
  }
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0 16px;
  animation: ${fadeUp} 0.5s ease both;

  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 9px;
    font-family: var(--font-display);
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--text);
  }

  svg {
    color: var(--accent);
  }

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: var(--border);
  }
`;

/* --------------------------- Sales row --------------------------- */
export const SalesRow = styled.section`
  margin-bottom: 24px;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: 240ms;
`;

/* --------------------------- Charts grid --------------------------- */
export const ChartsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: 320ms;

  & > .span-4 {
    grid-column: span 4;
  }
  & > .span-8 {
    grid-column: span 8;
  }
  & > .span-6 {
    grid-column: span 6;
  }
  & > .span-12 {
    grid-column: span 12;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
    & > * {
      grid-column: 1 / -1 !important;
    }
  }
`;
